import {UserStripeService} from "../../../stripe/service/UserStripeService";
import {StoreService} from "./StoreService";
import {StoreProductStates} from "../models/StoreProductState";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {StripeCardSummaryRecord} from "../../../stripe/models/StripeBillingRecord";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {Pair} from "../../../js/util/UtilFunctions";
import {ObservableWithLazyLatest} from "app/js/classes/lazy-observable/ObservableWithLazyLatest";

export abstract class StoreViewStore<T extends string> {

    protected initialState: StoreProductStates<T>;
    protected readonly productState$ = new ReplaySubject<StoreProductStates<T>>(1);
    protected readonly currentCard$ = new BehaviorSubject<StripeCardSummaryRecord | null>(null);
    private readonly updateState$ = new ReplaySubject<Partial<StoreProductStates<T>>>(1);
    private readonly toggleState$ = new ReplaySubject<T>(1);
    private readonly selectProduct$ = new ReplaySubject<T>(1);

    private readonly destroyed$ = new ReplaySubject<void>();

    protected constructor(protected storeService: StoreService<T>, protected userStripeService: UserStripeService) {
        this.init();
    }

    private init() {
        Observable.fromPromise(this.storeService.currentProductState())
                  .subscribe((state) => {
                      this.initialState = state;
                      this.productState$.next(state);
                  });

        Observable.fromPromise(this.userStripeService.billingDetails())
                  .map((billing) => billing.card)
                  .subscribe((card) => this.currentCard$.next(card));

        this.updateState$
            .withLatestFrom(this.productStates)
            .takeUntil(this.destroyed$)
            .map(([updated, previous]) => ObjectUtil.merge({}, previous, updated))
            .subscribe((state) => this.productState$.next(state));

        this.toggleState$
            .withLatestFrom(this.productStates)
            .takeUntil(this.destroyed$)
            .map(([product, states]) => Pair(product, states[product]))
            .filter(([product, state]) => !state.locked && !state.inBundle)
            .subscribe(([product, state]) => {
                const updated = ObjectUtil.merge({}, state, {selected: !state.selected});
                this.updateProductState({[product]: updated} as Partial<StoreProductStates<T>>);
            });

        ObservableWithLazyLatest.create(this.selectProduct$, this.productStates)
                                .observeOn(Scheduler.async)
                                .takeUntil(this.destroyed$)
                                .map(([product, states]) => Pair(product, states[product]))
                                .filter(([product, state]) => !state.locked && !state.inBundle)
                                .subscribe(([product, state]) => {
                                    const updated = ObjectUtil.merge({}, state, {selected: true});
                                    this.updateProductState({[product]: updated} as Partial<StoreProductStates<T>>);
                                });
    }

    public get productStates(): Observable<StoreProductStates<T>> {
        return this.productState$
                   .map((s) => ObjectUtil.copy(s))
                   .observeOn(Scheduler.async);
    }

    public get currentCard(): Observable<StripeCardSummaryRecord | null> {
        return this.currentCard$
                   .map((card) => ObjectUtil.copy(card))
                   .observeOn(Scheduler.async);
    }

    public toggleProductState(product: T) {
        this.toggleState$.next(product);
    }

    public setProductSelected(product: T) {
        this.selectProduct$.next(product);
    }

    private updateProductState(updated: Partial<StoreProductStates<T>>) {
        this.updateState$.next(updated);
    }

    protected updateState(state: StoreProductStates<T>) {
        this.productState$.next(state);
    }

    public updateCard(card: StripeCardSummaryRecord) {
        this.currentCard$.next(card);
    }

    public resetState() {
        this.productState$.next(this.initialState);
    }

    public destroy() {
        this.destroyed$.next(void 0);
        this.destroyed$.complete();
    }
}
